
/* ============================================================
   WAMBOLT PDP SECTION DOCK

   Automatically builds navigation from Shopify's current
   PDP section order.

   - Handles multiple System Part sections independently
   - Reads Boxes / Hitch from section headings
   - Supports optional data-wambolt-dock-label overrides
   - Keeps desktop Lenis scrolling
   - Keeps native mobile scrolling and popover
   - Preserves smooth GSAP animations
   - Supports Shopify editor and Barba lifecycle
   ============================================================ */

(function () {
  'use strict';

  const SECTION_RULES = [
    {
      selector: '[id^="shopify-section-"][id$="__main"]',
      label: 'RTG SYSTEM',
    },
    {
      selector: '[id^="shopify-section-"][id*="__wambolt_system_part_"]',
      part: true,
    },
    {
      selector: '[id^="shopify-section-"][id*="__wambolt_system_overview_"]',
      label: 'THE SYSTEM',
    },
    {
      selector: '[id^="shopify-section-"][id*="__wambolt_system_specifications_"]',
      label: 'SPECIFICATIONS',
    },
    {
      selector: '[id^="shopify-section-"][id*="__wambolt_compatibility_"]',
      label: 'COMPATIBILITY',
    },
    {
      selector: '[id^="shopify-section-"][id*="__wambolt_pdp_faq_"]',
      label: 'FAQ',
    },
  ];

  /* =========================================
     SECTION DISCOVERY
     ========================================= */

  function partLabel(section) {
    const headingSelectors = [
      '[data-wambolt-dock-title]',
      '.wambolt-system-part__title',
      '.wambolt-system-part__heading',
      'h1',
      'h2',
      'h3',
      'h4',
    ].join(', ');

    const headings = Array.from(
      section.querySelectorAll(headingSelectors)
    )
      .map((element) =>
        element.textContent.replace(/\s+/g, ' ').trim()
      )
      .filter(Boolean);

    const exactHeading = headings.find((heading) =>
      /^(?:the\s+)?(?:boxes?|hitch(?:\s+deck)?)$/i.test(heading)
    );

    const matching =
      exactHeading ||
      headings.find((heading) =>
        /\b(box(?:es)?|hitch|deck)\b/i.test(heading)
      );

    if (matching) {
      const firstMatch = matching.match(
        /\b(box(?:es)?|hitch|deck)\b/i
      )?.[1]?.toLowerCase();

      return firstMatch?.startsWith('box')
        ? 'THE BOXES'
        : 'THE HITCH';
    }

    return headings[0]?.toUpperCase() || 'SYSTEM PART';
  }

  function collectSections(main) {
    return Array.from(
      main.querySelectorAll('[id^="shopify-section-"]')
    )
      .map((section) => {
        const rule = SECTION_RULES.find((entry) =>
          section.matches(entry.selector)
        );

        const metadata = section.hasAttribute(
          'data-wambolt-dock-label'
        )
          ? section
          : section.querySelector(
              '[data-wambolt-dock-label]'
            );

        if (!rule && !metadata) return null;

        const visibility = metadata?.getAttribute(
          'data-wambolt-dock-visible'
        );

        if (visibility === 'false') return null;

        const override = metadata
          ?.getAttribute('data-wambolt-dock-label')
          ?.trim();

        const label =
          override ||
          (rule?.part ? partLabel(section) : rule?.label);

        return label
          ? { section, label: label.toUpperCase() }
          : null;
      })
      .filter(Boolean);
  }

  function buildLinks(list, entries) {
    const items = list.querySelector(
      '.section-dock__items'
    );

    if (!items) return [];

    items.replaceChildren();

    return entries.map(({ section, label }, index) => {
      const li = document.createElement('li');
      const link = document.createElement('a');

      link.className = 'section-dock__link';
      link.setAttribute('data-section-dock-link', '');
      link.href = `#${section.id}`;

      const number = document.createElement('span');
      number.className = 'section-dock__link-num';
      number.textContent = String(index + 1).padStart(
        2,
        '0'
      );

      const opening = document.createElement('span');
      opening.className = 'section-dock__bracket';
      opening.setAttribute('aria-hidden', 'true');
      opening.textContent = '[';

      const name = document.createElement('span');
      name.className = 'section-dock__link-name';
      name.textContent = label;

      const closing = opening.cloneNode(false);
      closing.textContent = ']';

      link.append(number, opening, name, closing);

      li.appendChild(link);
      items.appendChild(li);

      section.setAttribute(
        'data-wambolt-dock-section',
        ''
      );

      return { link, section };
    });
  }

  /* =========================================
     GLOBAL STATE
     ========================================= */

  let session = null;
  let checkScheduled = false;

  const reduceMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  );

  const canHover = window.matchMedia(
    '(hover: hover)'
  );

  function isMobileViewport() {
    return window.matchMedia(
      '(max-width: 749px)'
    ).matches;
  }

  function getScroller() {
    const wrapper = document.querySelector(
      '.page-wrapper'
    );

    if (!wrapper) return window;

    const style = window.getComputedStyle(wrapper);

    const canScroll =
      wrapper.scrollHeight > wrapper.clientHeight + 8;

    const overflowPattern = isMobileViewport()
      ? /auto|scroll/
      : /auto|scroll|hidden/;

    const hasScrollableOverflow =
      overflowPattern.test(style.overflowY);

    return hasScrollableOverflow && canScroll
      ? wrapper
      : window;
  }

  function getHeaderOffset() {
    const height = parseFloat(
      window
        .getComputedStyle(document.body)
        .getPropertyValue('--header-group-height')
    );

    return Math.max(
      24,
      Math.min(
        (Number.isFinite(height) ? height : 0) + 24,
        160
      )
    );
  }

  function getLenisInstance() {
    const candidates = [
      window.wamboltLenis,
      window.WamboltLenis?.instance,
      window.WamboltLenis?.lenis,
      window.lenis,
      window.lenisInstance,
      window.Wambolt?.lenis,
    ];

    return candidates.find(
      (candidate) =>
        candidate &&
        typeof candidate.scrollTo === 'function'
    );
  }

  function scrollToSection(section) {
    const offset = getHeaderOffset();
    const lenis = getLenisInstance();

    if (lenis && !isMobileViewport()) {
      lenis.scrollTo(section, {
        offset: -offset,
        duration: reduceMotion.matches ? 0 : 1.0,
        immediate: reduceMotion.matches,
      });

      return;
    }

    const scroller = getScroller();

    const behavior = reduceMotion.matches
      ? 'instant'
      : 'smooth';

    if (scroller === window) {
      const top =
        window.scrollY +
        section.getBoundingClientRect().top -
        offset;

      window.scrollTo({
        top: Math.max(0, top),
        behavior,
      });
    } else {
      const top =
        scroller.scrollTop +
        section.getBoundingClientRect().top -
        scroller.getBoundingClientRect().top -
        offset;

      scroller.scrollTo({
        top: Math.max(0, top),
        behavior,
      });
    }
  }

  /* =========================================
     DESTROY
     ========================================= */

  function destroyDock() {
    if (!session) return;

    const state = session;
    session = null;

    state.controller.abort();
    state.sectionObserver?.disconnect();
    state.killDockTimeline?.();

    state.triggers.forEach((trigger) =>
      trigger.kill()
    );

    state.animations.forEach((animation) =>
      animation?.kill()
    );

    state.sections.forEach((section) =>
      section.removeAttribute(
        'data-wambolt-dock-section'
      )
    );

    if (window.gsap) {
      window.gsap.killTweensOf(state.pill);
      window.gsap.killTweensOf(state.dock);
      window.gsap.killTweensOf(state.labelWrap);
      window.gsap.killTweensOf(state.indicator);
      window.gsap.killTweensOf(state.rect);
      window.gsap.killTweensOf(state.links);
    }

    if (state.placeholder.isConnected) {
      state.placeholder.replaceWith(state.mount);
    } else {
      state.mount.remove();
      state.placeholder.remove();
    }
  }

  /* =========================================
     INITIALIZATION
     ========================================= */

  function initSectionAnchorDock(mount) {
    const gsap = window.gsap;
    const ScrollTrigger = window.ScrollTrigger;

    if (!gsap || !ScrollTrigger) return;

    const dock = mount.querySelector(
      '[data-section-dock-init]'
    );

    const pill = dock?.querySelector(
      '[data-section-dock-pill]'
    );

    const toggle = dock?.querySelector(
      '[data-section-dock-toggle]'
    );

    const labelWrap = dock?.querySelector(
      '[data-section-dock-label-wrap]'
    );

    const list = dock?.querySelector(
      '[data-section-dock-list]'
    );

    const indicator = dock?.querySelector(
      '[data-section-dock-indicator]'
    );

    const main = mount.closest(
      'main[data-barba="container"]'
    );

    if (
      !main ||
      !dock ||
      !pill ||
      !toggle ||
      !labelWrap ||
      !list ||
      !indicator
    ) {
      return;
    }

    const entries = collectSections(main);

    if (entries.length < 2) return;

    const pairs = buildLinks(list, entries);

    const links = pairs.map((pair) => pair.link);
    const sections = pairs.map((pair) => pair.section);

    gsap.registerPlugin(ScrollTrigger);

    const placeholder = document.createComment(
      'Wambolt PDP dock location'
    );

    mount.parentNode.insertBefore(
      placeholder,
      mount
    );

    document.body.appendChild(mount);
    mount.setAttribute('data-ready', '');

    const controller = new AbortController();
    const { signal } = controller;

    const triggers = [];
    const animations = [];

    const rect = {
      x: 0,
      y: 0,
      w: 0,
      h: 0,
    };

    const state = {
      mount,
      placeholder,
      dock,
      pill,
      toggle,
      list,
      labelWrap,
      indicator,
      links,
      sections,
      controller,
      triggers,
      animations,
      rect,
    };

    session = state;

    let activeIndex = 0;
    let open = false;
    let isHidden = true;
    let dockTl = null;

    state.killDockTimeline = () => dockTl?.kill();

    let rangeTrigger = null;
    let hideTriggers = [];

    const motion = 'smooth';

    const smooth = () =>
      motion === 'smooth' || reduceMotion.matches;

    const dur = (d) =>
      reduceMotion.matches ? 0 : d;

    const ease = (bouncy, calm) =>
      smooth() ? calm : bouncy;

    const initializedMobile = isMobileViewport();
    const scroller = getScroller();

    const scrollerOption =
      scroller === window ? {} : { scroller };

    gsap.set(pill, {
      transformOrigin: '50% 100%',
    });

    gsap.set(dock, {
      yPercent: 40,
      autoAlpha: 0,
    });

    dock.toggleAttribute('data-hidden', true);
    list.inert = true;

    /* =========================================
       ACTIVE LINK STATE
       ========================================= */

    function syncLinkState() {
      links.forEach((link, index) => {
        link.toggleAttribute(
          'data-active',
          index === activeIndex
        );

        if (index === activeIndex) {
          link.setAttribute(
            'aria-current',
            'location'
          );
        } else {
          link.removeAttribute('aria-current');
        }
      });
    }

    /* =========================================
       COLLAPSED LABEL
       ========================================= */

    const labelTemplate = labelWrap.firstElementChild;

    function buildLabel(index) {
      const span = labelTemplate.cloneNode(false);
      span.innerHTML = links[index].innerHTML;
      return span;
    }

    function setLabel(index) {
      gsap.killTweensOf(labelWrap);

      Array.from(labelWrap.children).forEach(
        (child) => {
          gsap.killTweensOf(child);
        }
      );

      labelWrap.textContent = '';
      labelWrap.appendChild(buildLabel(index));

      gsap.set(labelWrap, {
        width: 'auto',
      });
    }

    function swapLabel(index, movingDown) {
      const olds = Array.from(labelWrap.children);
      const startWidth = labelWrap.offsetWidth;

      gsap.killTweensOf(labelWrap);

      olds.forEach((old) => {
        gsap.killTweensOf(old);

        gsap.set(old, {
          position: 'absolute',
          top: 0,
          left: 0,
        });

        gsap.to(old, {
          yPercent: movingDown ? -120 : 120,
          autoAlpha: 0,
          duration: dur(0.35),
          ease: 'power2.out',
          onComplete: () => old.remove(),
        });
      });

      const next = buildLabel(index);
      labelWrap.appendChild(next);

      gsap.set(labelWrap, {
        width: 'auto',
      });

      const targetWidth = next.offsetWidth;

      gsap.fromTo(
        labelWrap,
        { width: startWidth },
        {
          width: targetWidth,
          duration: dur(0.45),
          ease: ease(
            'back.out(1.6)',
            'power3.out'
          ),
        }
      );

      gsap.fromTo(
        next,
        {
          yPercent: movingDown ? 120 : -120,
          autoAlpha: 0,
        },
        {
          yPercent: 0,
          autoAlpha: 1,
          duration: dur(0.45),
          ease: 'power3.out',
        }
      );

      if (!smooth()) {
        gsap.killTweensOf(pill, 'scaleY');

        const animation = gsap
          .timeline()
          .to(pill, {
            scaleY: 0.85,
            duration: 0.15,
            ease: 'power2.out',
          })
          .to(pill, {
            scaleY: 1,
            duration: 0.45,
            ease: 'back.out(2.5)',
          });

        animations.push(animation);
      }
    }

    /* =========================================
       ACTIVE INDICATOR
       ========================================= */

    function rectFor(link) {
      return {
        x: link.offsetLeft,
        y: link.offsetTop,
        w: link.offsetWidth,
        h: link.offsetHeight,
      };
    }

    function render() {
      gsap.set(indicator, {
        x: rect.x,
        y: rect.y,
        width: rect.w,
        height: rect.h,
      });
    }

    function placeIndicator(index) {
      Object.assign(
        rect,
        rectFor(links[index])
      );

      gsap.killTweensOf(rect);
      gsap.killTweensOf(indicator);

      gsap.set(indicator, {
        scaleX: 1,
        scaleY: 1,
      });

      render();
    }

    function hopIndicator(index) {
      const target = rectFor(links[index]);

      gsap.killTweensOf(rect);
      gsap.killTweensOf(indicator);

      if (smooth()) {
        gsap.set(indicator, {
          scaleX: 1,
          scaleY: 1,
        });

        gsap.to(rect, {
          x: target.x,
          y: target.y,
          w: target.w,
          h: target.h,
          duration: dur(0.35),
          ease: 'power3.out',
          onUpdate: render,
        });

        return;
      }

      const delta = target.y - rect.y;
      const sign = delta === 0 ? 1 : Math.sign(delta);

      const overshoot =
        gsap.utils.clamp(
          6,
          12,
          Math.abs(delta) * 0.08
        ) * sign;

      const apexY = gsap.utils.clamp(
        0,
        list.clientHeight - target.h,
        target.y + overshoot
      );

      gsap.set(indicator, {
        transformOrigin: '50% 50%',
      });

      const timeline = gsap
        .timeline()
        .to(rect, {
          x: target.x,
          y: apexY,
          w: target.w,
          h: target.h,
          duration: 0.35,
          ease: 'power3.out',
          onUpdate: render,
        })
        .to(rect, {
          y: target.y,
          duration: 0.25,
          ease: 'power2.inOut',
          onUpdate: render,
        });

      const squash = gsap
        .timeline()
        .to(indicator, {
          scaleX: 0.78,
          duration: 0.15,
          ease: 'power2.out',
        })
        .to(indicator, {
          scaleY: 1,
          scaleX: 1,
          duration: 0.45,
          ease: 'back.out(2.5)',
        });

      animations.push(timeline, squash);
    }

    /* =========================================
       SET ACTIVE SECTION
       ========================================= */

    function setActive(index, movingDown) {
      if (index === activeIndex) return;

      activeIndex = index;
      syncLinkState();

      if (open) {
        setLabel(index);
        hopIndicator(index);
      } else {
        swapLabel(index, movingDown);
        placeIndicator(index);
      }
    }

    /* =========================================
       OPEN DOCK
       ========================================= */

    function openDock() {
      if (open || isHidden) return;

      open = true;
      list.inert = false;

      toggle.setAttribute(
        'aria-expanded',
        'true'
      );

      placeIndicator(activeIndex);

      const fromW = pill.offsetWidth;
      const fromH = pill.offsetHeight;

      dockTl?.kill();

      if (isMobileViewport()) {
        gsap.killTweensOf([
          pill,
          toggle,
          list,
          ...links,
        ]);

        gsap.set(pill, {
          clearProps: 'width,height',
        });

        gsap.set(toggle, {
          clearProps:
            'opacity,visibility,pointerEvents',
        });

        gsap.set(list, {
          clearProps:
            'opacity,visibility,pointerEvents,transform',
        });

        gsap.set(links, {
          yPercent: 0,
          opacity: 1,
        });

        return;
      }

      gsap.set(list, {
        visibility: 'inherit',
      });

      gsap.set(pill, {
        width: fromW,
        height: fromH,
      });

      gsap.set(links[activeIndex], {
        yPercent: 0,
        opacity: 1,
      });

      dockTl = gsap
        .timeline()
        .to(
          pill,
          {
            width: list.offsetWidth,
            height: list.offsetHeight,
            duration: dur(0.45),
            ease: ease(
              'back.out(1.4)',
              'power3.out'
            ),
          },
          0
        )
        .to(
          toggle,
          {
            autoAlpha: 0,
            duration: dur(0.15),
            ease: 'power1.out',
          },
          0
        )
        .to(
          list,
          {
            opacity: 1,
            duration: dur(0.25),
            ease: 'power1.out',
          },
          dur(0.05)
        )
        .fromTo(
          links.filter(
            (link, i) => i !== activeIndex
          ),
          {
            yPercent: 40,
            opacity: 0,
          },
          {
            yPercent: 0,
            opacity: 1,
            duration: dur(0.25),
            ease: 'power2.out',
            stagger: {
              each: dur(0.05),
              from: 'end',
            },
          },
          dur(0.05)
        );
    }

    /* =========================================
       CLOSE DOCK
       ========================================= */

    function closeDock() {
      if (!open) return;

      open = false;
      list.inert = true;

      toggle.setAttribute(
        'aria-expanded',
        'false'
      );

      dockTl?.kill();

      if (isMobileViewport()) {
        gsap.killTweensOf([
          pill,
          toggle,
          list,
          ...links,
        ]);

        gsap.set(pill, {
          clearProps: 'width,height',
        });

        gsap.set(toggle, {
          clearProps:
            'opacity,visibility,pointerEvents',
        });

        gsap.set(list, {
          clearProps:
            'opacity,visibility,pointerEvents,transform',
        });

        gsap.set(links, {
          yPercent: 0,
          opacity: 1,
        });

        return;
      }

      dockTl = gsap
        .timeline({
          onComplete: () => {
            gsap.set(pill, {
              clearProps: 'width,height',
            });

            gsap.set(list, {
              visibility: 'hidden',
            });

            gsap.set(links, {
              yPercent: 0,
              opacity: 1,
            });
          },
        })
        .to(
          pill,
          {
            width: toggle.offsetWidth + 14,
            height: toggle.offsetHeight + 14,
            duration: dur(0.3),
            ease: 'power3.out',
          },
          0
        )
        .to(
          list,
          {
            opacity: 0,
            duration: dur(0.15),
            ease: 'power1.out',
          },
          0
        )
        .to(
          toggle,
          {
            autoAlpha: 1,
            duration: dur(0.25),
            ease: 'power1.out',
          },
          dur(0.1)
        );
    }

    /* =========================================
       DESKTOP SECTION TRACKING
       ========================================= */

    sections.forEach((section, index) => {
      triggers.push(
        ScrollTrigger.create({
          ...scrollerOption,
          trigger: section,
          start: 'top 45%',
          end: 'bottom 45%',
          onToggle: (self) => {
            if (
              !isMobileViewport() &&
              self.isActive
            ) {
              setActive(
                index,
                self.direction !== -1
              );
            }
          },
        })
      );
    });

    /* =========================================
       VISIBILITY
       ========================================= */

    function updateHidden() {
      let inRange;
      let inHideZone;

      if (isMobileViewport()) {
        const line =
          window.innerHeight * 0.45;

        const first =
          sections[0].getBoundingClientRect();

        const last =
          sections[
            sections.length - 1
          ].getBoundingClientRect();

        inRange =
          first.top <= line &&
          last.bottom >= line;

        inHideZone = Array.from(
          document.querySelectorAll(
            '[data-section-dock-hide]'
          )
        ).some((zone) => {
          const rect =
            zone.getBoundingClientRect();

          const offset = parseFloat(
            zone.getAttribute(
              'data-section-dock-hide'
            )
          );

          const threshold =
            window.innerHeight *
            (
              1 -
              (
                Number.isNaN(offset)
                  ? 10
                  : Math.max(
                      0,
                      Math.min(offset, 100)
                    )
              ) / 100
            );

          return (
            rect.top <= threshold &&
            rect.bottom >= 0
          );
        });
      } else {
        inRange = rangeTrigger
          ? rangeTrigger.isActive
          : true;

        inHideZone = hideTriggers.some(
          (trigger) => trigger.isActive
        );
      }

      const hidden =
        !inRange || inHideZone;

      if (hidden === isHidden) return;

      isHidden = hidden;

      if (hidden && open) {
        closeDock();
      }

      dock.toggleAttribute(
        'data-hidden',
        hidden
      );

      gsap.to(dock, {
        yPercent: hidden ? 40 : 0,
        autoAlpha: hidden ? 0 : 1,
        duration: dur(0.3),
        ease: hidden
          ? 'power2.out'
          : ease(
              'back.out(1.4)',
              'power3.out'
            ),
      });
    }

    rangeTrigger = ScrollTrigger.create({
      ...scrollerOption,
      trigger: sections[0],
      endTrigger:
        sections[sections.length - 1],
      start: 'top 45%',
      end: 'bottom 45%',
      onToggle: updateHidden,
    });

    triggers.push(rangeTrigger);

    hideTriggers = Array.from(
      document.querySelectorAll(
        '[data-section-dock-hide]'
      )
    ).map((zone) => {
      const offset = parseFloat(
        zone.getAttribute(
          'data-section-dock-hide'
        )
      );

      const line =
        100 -
        gsap.utils.clamp(
          0,
          100,
          Number.isNaN(offset) ? 10 : offset
        );

      const trigger = ScrollTrigger.create({
        ...scrollerOption,
        trigger: zone,
        start: `top ${line}%`,
        end: 'bottom top',
        onToggle: updateHidden,
      });

      triggers.push(trigger);
      return trigger;
    });

    /* =========================================
       MOBILE ACTIVE SECTION TRACKING
       ========================================= */

    let positionFrame = 0;

    function updateMobilePosition() {
      if (
        session !== state ||
        !isMobileViewport()
      ) {
        return;
      }

      const line =
        window.innerHeight * 0.45;

      let next = 0;

      sections.forEach(
        (section, index) => {
          if (
            section.getBoundingClientRect().top <=
            line
          ) {
            next = index;
          }
        }
      );

      if (
        next !== activeIndex &&
        !open
      ) {
        setActive(
          next,
          next > activeIndex
        );
      }

      updateHidden();
    }

    function requestPositionCheck() {
      if (positionFrame) return;

      positionFrame =
        window.requestAnimationFrame(() => {
          positionFrame = 0;
          updateMobilePosition();
        });
    }

    window.addEventListener(
      'scroll',
      requestPositionCheck,
      {
        passive: true,
        signal,
      }
    );

    document.addEventListener(
      'scroll',
      requestPositionCheck,
      {
        passive: true,
        capture: true,
        signal,
      }
    );

    /* =========================================
       LINK INTERACTIONS
       ========================================= */

    links.forEach((link, index) => {
      link.addEventListener(
        'click',
        (event) => {
          event.preventDefault();

          closeDock();

          setActive(
            index,
            index > activeIndex
          );

          scrollToSection(
            sections[index]
          );

          if (
            window.history?.replaceState
          ) {
            window.history.replaceState(
              null,
              '',
              `#${sections[index].id}`
            );
          }
        },
        { signal }
      );
    });

    toggle.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        event.stopPropagation();

        if (open) {
          closeDock();
        } else {
          openDock();
        }

        if (
          open &&
          event.detail === 0 &&
          !isMobileViewport()
        ) {
          links[activeIndex].focus();
        }
      },
      { signal }
    );

    if (
      canHover.matches &&
      !isMobileViewport()
    ) {
      dock.addEventListener(
        'mouseenter',
        openDock,
        { signal }
      );

      dock.addEventListener(
        'mouseleave',
        closeDock,
        { signal }
      );
    }

    document.addEventListener(
      'keydown',
      (event) => {
        if (
          event.key === 'Escape' &&
          open
        ) {
          closeDock();
          toggle.focus();
        }
      },
      { signal }
    );

    document.addEventListener(
      'click',
      (event) => {
        if (
          open &&
          !dock.contains(event.target)
        ) {
          closeDock();
        }
      },
      { signal }
    );

    dock.addEventListener(
      'focusout',
      (event) => {
        if (isMobileViewport()) return;

        if (
          open &&
          event.relatedTarget &&
          !dock.contains(
            event.relatedTarget
          )
        ) {
          closeDock();
        }
      },
      { signal }
    );

    /* =========================================
       LAYOUT AND RESPONSIVE LIFECYCLE
       ========================================= */

    function refreshLayout() {
      if (session !== state) return;

      setLabel(activeIndex);
      placeIndicator(activeIndex);

      if (
        open &&
        !isMobileViewport()
      ) {
        gsap.set(pill, {
          width: list.offsetWidth,
          height: list.offsetHeight,
        });
      }
    }

    window.addEventListener(
      'resize',
      () => {
        if (
          initializedMobile !==
          isMobileViewport()
        ) {
          destroyDock();
          scheduleDockCheck();
          return;
        }

        refreshLayout();
        ScrollTrigger.refresh();
        requestPositionCheck();
      },
      { signal }
    );

    document.fonts?.ready?.then(() => {
      if (session !== state) return;

      refreshLayout();
      ScrollTrigger.refresh();
      requestPositionCheck();
    });

    /* =========================================
       SHOPIFY SECTION ORDER CHANGES
       ========================================= */

    const sectionObserver =
      new MutationObserver((mutations) => {
        const sectionChanged =
          mutations.some((mutation) => {
            const changedNodes = [
              ...mutation.addedNodes,
              ...mutation.removedNodes,
            ];

            return changedNodes.some(
              (node) =>
                node.nodeType === 1 &&
                node.matches?.(
                  '[id^="shopify-section-"]'
                )
            );
          });

        if (sectionChanged) {
          scheduleDockRebuild();
        }
      });

    sectionObserver.observe(main, {
      childList: true,
    });

    state.sectionObserver =
      sectionObserver;

    syncLinkState();
    refreshLayout();
    ScrollTrigger.refresh();
    requestPositionCheck();
    updateHidden();
  }

  /* =========================================
     ENSURE DOCK
     ========================================= */

  function ensureDock() {
    checkScheduled = false;

    if (
      session &&
      !session.placeholder.isConnected
    ) {
      destroyDock();
    }

    if (session) return;

    const mount = document.querySelector(
      'main[data-barba="container"] [data-wambolt-section-dock-mount]'
    );

    if (!mount) return;

    initSectionAnchorDock(mount);
  }

  function scheduleDockCheck() {
    if (checkScheduled) return;

    checkScheduled = true;

    window.requestAnimationFrame(
      ensureDock
    );
  }

  function scheduleDockRebuild() {
    destroyDock();
    scheduleDockCheck();
  }

  /* =========================================
     GLOBAL LIFECYCLE
     ========================================= */

  function start() {
    scheduleDockCheck();

    const wrapper = document.querySelector(
      '[data-barba="wrapper"]'
    );

    if (wrapper) {
      const observer =
        new MutationObserver(
          scheduleDockCheck
        );

      observer.observe(wrapper, {
        childList: true,
      });
    }

    document.addEventListener(
      'wambolt:page:before-leave',
      destroyDock
    );

    document.addEventListener(
      'wambolt:page:enter',
      scheduleDockCheck
    );

    document.addEventListener(
      'shopify:section:load',
      scheduleDockRebuild
    );

    document.addEventListener(
      'shopify:section:unload',
      scheduleDockRebuild
    );

    document.addEventListener(
      'shopify:section:reorder',
      scheduleDockRebuild
    );
  }

  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      start,
      { once: true }
    );
  } else {
    start();
  }
})();
